import { defineStore } from 'pinia';
import { computed, onUnmounted, ref, watch, type ComputedRef, type Ref } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';

type LoadingRef = Ref<boolean> | ComputedRef<boolean>;

const watchLoadingRefs: LoadingRef[] = [];
const watchRefreshingRefs: LoadingRef[] = [];

export const useLoadingStore = defineStore('loading', () => {
  /** Something the user is waiting for: a pending mutation or a first load without data. */
  const loading = ref(false);
  /** A background refetch of data that is already on screen (polling, refresh buttons). */
  const refreshing = ref(false);

  const updateLoading = (loadingRefs: LoadingRef[]) => {
    loading.value = loadingRefs.some((loadingRef) => loadingRef.value);
  };

  const updateRefreshing = (refreshingRefs: LoadingRef[]) => {
    refreshing.value = refreshingRefs.some((refreshingRef) => refreshingRef.value);
  };

  /** Any request in flight. For progress display only — never to disable interactive elements. */
  const busy = computed(() => loading.value || refreshing.value);

  return {
    loading,
    refreshing,
    busy,
    updateLoading,
    updateRefreshing,
  };
});

const register = (
  trackedRefs: LoadingRef[],
  update: (trackedRefs: LoadingRef[]) => void,
  refs: LoadingRef[],
) => {
  trackedRefs.push(...refs);

  const unwatch = watch(refs, () => update(trackedRefs), { immediate: true });

  return () => {
    unwatch();
    for (const ref of refs) {
      const index = trackedRefs.indexOf(ref);
      if (index !== -1) trackedRefs.splice(index, 1);
    }

    update(trackedRefs);
  };
};

export const registerLoading = (...loadingRefs: LoadingRef[]) => {
  const store = useLoadingStore();

  return register(watchLoadingRefs, (refs) => store.updateLoading(refs), loadingRefs);
};

export const registerRefreshing = (...refreshingRefs: LoadingRef[]) => {
  const store = useLoadingStore();

  return register(watchRefreshingRefs, (refs) => store.updateRefreshing(refs), refreshingRefs);
};

export const useLoading = () => {
  const store = usePiniaRefStore(useLoadingStore());

  const watchLoading = (...refs: LoadingRef[]) => {
    const cleanUp = registerLoading(...refs);

    onUnmounted(() => cleanUp());
  };

  const watchRefreshing = (...refs: LoadingRef[]) => {
    const cleanUp = registerRefreshing(...refs);

    onUnmounted(() => cleanUp());
  };

  return { ...store, watchLoading, watchRefreshing };
};
